'use client';

import { c as _c } from "react/compiler-runtime";
import { formatLongString } from '@morpho-org/morpho-ts';
import { Avatar } from '@morpho-org/ui-v6/client';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button, type ButtonProps } from '@repo/ui';
import { LoaderGradient } from '@repo/ui/icons';
import { abbreviateAddress } from '@repo/utils';
import { useSyncExternalStore } from 'react';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useIsMedium } from '@/hooks/useBreakpoints';
import WalletDropdown from './WalletDropdown';

// Desktop and mobile headers each mount a WalletButton; share the pending flag so neither
// can start a second open while the other is waiting.
let isOpeningModal = false;
const openingListeners = new Set<() => void>();
const setOpeningModal = (value: boolean) => {
  isOpeningModal = value;
  openingListeners.forEach(listener => listener());
};
const subscribeOpening = (listener: () => void) => {
  openingListeners.add(listener);
  return () => {
    openingListeners.delete(listener);
  };
};
interface WalletButtonProps {
  variant?: ButtonProps['variant'];
}

/** @internal */
export const WalletButton = t0 => {
  const $ = _c(33);
  const {
    variant
  } = t0;
  const isOpening = useSyncExternalStore(subscribeOpening, _temp, _temp2);
  const {
    modal,
    account: t1
  } = useWeb3Context();
  const {
    address,
    ens,
    ensImage
  } = t1;
  const isMedium = useIsMedium();
  if (address) {
    const t2 = ensImage ?? undefined;
    const t3 = ensImage ? undefined : address;
    let t4;
    if ($[0] !== t2 || $[1] !== t3) {
      t4 = {
        ensUrl: t2,
        address: t3,
        size: "medium"
      };
      $[0] = t2;
      $[1] = t3;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    const avatarProps = t4 as const;
    let t5;
    if ($[3] !== address || $[4] !== ens || $[5] !== ensImage) {
      t5 = {
        address,
        ens,
        ensImage
      };
      $[3] = address;
      $[4] = ens;
      $[5] = ensImage;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== avatarProps) {
      t6 = <Avatar {...avatarProps} />;
      $[7] = avatarProps;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== address || $[10] !== ens || $[11] !== isMedium) {
      t7 = !isMedium && (ens ? formatLongString(ens, 20) : abbreviateAddress(address));
      $[9] = address;
      $[10] = ens;
      $[11] = isMedium;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== t6 || $[14] !== t7) {
      t8 = <Button className="lg:pr-sm lg:py-0 lg:pl-[10px]" variant="ghost" size="md" data-testid="wallet-dropdown">{t6}{t7}</Button>;
      $[13] = t6;
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t5 || $[17] !== t8) {
      t9 = <WalletDropdown user={t5} trigger={t8} />;
      $[16] = t5;
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    return t9;
  }
  let t2;
  if ($[19] !== modal) {
    t2 = async () => {
      if (isOpeningModal) {
        return;
      }
      setOpeningModal(true);
      ;
      try {
        await modal.open();
        setOpeningModal(false);
      } catch (t3) {
        const error = t3;
        setOpeningModal(false);
        throw error;
      }
    };
    $[19] = modal;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  const openModal = t2;
  let t3;
  if ($[21] !== openModal) {
    t3 = () => void openModal();
    $[21] = openModal;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const t4 = isOpening || undefined;
  const t5 = isOpening && "text-transparent transition-none";
  let t6;
  if ($[23] !== t5) {
    t6 = cn("relative rounded-lg", t5);
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== isOpening) {
    t7 = isOpening && <LoaderGradient aria-hidden={true} className="absolute inset-0 m-auto" />;
    $[25] = isOpening;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t3 || $[28] !== t4 || $[29] !== t6 || $[30] !== t7 || $[31] !== variant) {
    t8 = <Button variant={variant} size="md" onClick={t3} aria-busy={t4} className={t6}>Connect{t7}</Button>;
    $[27] = t3;
    $[28] = t4;
    $[29] = t6;
    $[30] = t7;
    $[31] = variant;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  return t8;
};
export default WalletButton;
function _temp() {
  return isOpeningModal;
}
function _temp2() {
  return false;
}